import { onDestroy } from "@/modules/_";
import { Scroll } from "@lib/scroll";
import { Resize } from "@lib/subs";

const DEFAULT_CONFIG = {
  richText: "#postContent",
  toc: "#postTOC",
  progressBar: "#scroll-progress",
  headings: "h2, h3",
  activeOffset: 0.95,
  scrollOffset: -100,
  mobileScrollOffset: -460,
  mobileBreakpoint: 768,
};

function slugify(text = "") {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function uniqueId(base) {
  let id = base || "section";
  let index = 2;

  while (document.getElementById(id)) {
    id = `${base || "section"}-${index++}`;
  }

  return id;
}

function getNumber(value, fallback) {
  if (value === undefined) return fallback;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function findElement(root, selector) {
  if (!selector) return null;
  if (root.matches?.(selector)) return root;
  return root.querySelector(selector) || document.querySelector(selector);
}

export class BlogTOC {
  constructor(element, dataset = {}) {
    this.element = element;
    this.dataset = dataset;
    this.config = {
      richText: dataset.richText || DEFAULT_CONFIG.richText,
      toc: dataset.toc || DEFAULT_CONFIG.toc,
      progressBar: dataset.progressBar || DEFAULT_CONFIG.progressBar,
      headings: dataset.headings || DEFAULT_CONFIG.headings,
      activeOffset: getNumber(dataset.activeOffset, DEFAULT_CONFIG.activeOffset),
      scrollOffset: getNumber(dataset.scrollOffset, DEFAULT_CONFIG.scrollOffset),
      mobileScrollOffset: getNumber(dataset.mobileScrollOffset, DEFAULT_CONFIG.mobileScrollOffset),
      mobileBreakpoint: getNumber(dataset.mobileBreakpoint, DEFAULT_CONFIG.mobileBreakpoint),
    };

    this.tocLinks = [];
    this.sections = [];
    this.toggleButton = null;
    this.isOpen = false;
    this.cleanupScroll = null;
    this.cleanupResize = null;
    this.linkListeners = [];
    this.imageListeners = [];
    this.contentResizeObserver = null;
    this.refreshFrame = 0;

    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.refreshLayout = this.refreshLayout.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
  }

  init() {
    if (!this.element) return;

    this.richText = findElement(this.element, this.config.richText);
    this.tocElement = findElement(this.element, this.config.toc);

    if (!this.richText || !this.tocElement) return;

    this.progressBar = findElement(this.element, this.config.progressBar);

    this.buildTOC();
    this.setupLinkNavigation();
    this.setupContentObservers();
    this.updateScrollState({
      scroll: Scroll.scroll,
      limit: Scroll.limit,
      progress: Scroll.progress,
    });

    this.cleanupScroll = Scroll.add(this.handleScroll);
    this.cleanupResize = Resize.add(this.handleResize);
  }

  buildTOC() {
    const headings = Array.from(
      this.richText.querySelectorAll(this.config.headings)
    );

    if (!headings.length) {
      this.tocElement.style.display = "none";
      this.tocElement.replaceChildren();
      this.tocLinks = [];
      this.sections = [];
      return;
    }

    this.tocElement.style.display = "";

    headings.forEach((heading) => {
      if (!heading.id) {
        heading.id = uniqueId(slugify(heading.textContent || ""));
      }
    });

    const hasH2 = headings.some((h) => h.tagName === "H2");
    const isTopLevel = (heading) => !hasH2 || heading.tagName === "H2";

    const tree = [];
    let currentParent = null;

    headings.forEach((heading) => {
      if (isTopLevel(heading) || !currentParent) {
        currentParent = {
          el: heading,
          children: [],
        };
        tree.push(currentParent);
        return;
      }

      currentParent.children.push(heading);
    });

    const list = document.createElement("ul");
    list.className = "toc-nav";

    let parentIndex = 0;

    tree.forEach((node) => {
      parentIndex += 1;

      list.appendChild(
        this.createItem(
          node.el,
          String(parentIndex).padStart(2, "0")
        )
      );

      node.children.forEach((child) => {
        list.appendChild(this.createItem(child, "&#8627;", "sub"));
      });
    });

    if (!this.toggleButton) {
      this.toggleButton = document.createElement("button");
      this.toggleButton.className = "toc-toggle";
      this.toggleButton.setAttribute("aria-expanded", "false");
      this.toggleButton.innerHTML =
        '<span class="toc-toggle-label">Table of Contents</span>' +
        '<svg class="toc-toggle-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">' +
        '<path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
        "</svg>";
      this.toggleButton.addEventListener("click", () => this.toggle());
    }

    this.tocElement.replaceChildren(this.toggleButton, list);
    this.tocLinks = Array.from(list.querySelectorAll("a[data-id]"));
    this.sections = this.tocLinks
      .map((link) => document.getElementById(link.dataset.id || ""))
      .filter(Boolean);
  }

  createItem(heading, label, className = "") {
    const item = document.createElement("li");

    if (className) {
      item.className = className;
    }

    const link = document.createElement("a");
    link.href = `#${heading.id}`;
    link.dataset.id = heading.id;

    const number = document.createElement("span");
    number.className = "num";
    number.innerHTML = label;

    link.append(number, document.createTextNode(heading.textContent || ""));
    item.appendChild(link);

    return item;
  }

  setupLinkNavigation() {
    this.linkListeners.forEach(({ element, handler }) => {
      element.removeEventListener("click", handler);
    });
    this.linkListeners = [];

    this.tocLinks.forEach((link) => {
      const handler = (event) => {
        const targetId = link.dataset.id;
        if (!targetId) return;

        event.preventDefault();
        event.stopPropagation();

        if (window.location.hash !== `#${targetId}`) {
          history.pushState(null, "", `#${targetId}`);
        }

        this.scrollToHeading(targetId);
        this.close();
        this.updateActiveTOC();
      };

      link.addEventListener("click", handler);
      this.linkListeners.push({ element: link, handler });
    });
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.isOpen = true;
    this.tocElement.classList.add("toc-open");
    this.toggleButton?.setAttribute("aria-expanded", "true");
    document.addEventListener("click", this.handleOutsideClick);
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.tocElement.classList.remove("toc-open");
    this.toggleButton?.setAttribute("aria-expanded", "false");
    document.removeEventListener("click", this.handleOutsideClick);
  }

  handleOutsideClick(event) {
    if (!this.tocElement.contains(event.target)) {
      this.close();
    }
  }

  scrollToHeading(targetId) {
    const target = document.getElementById(targetId);
    if (!target) return;

    const isMobile = window.innerWidth < this.config.mobileBreakpoint;
    const offset = isMobile ? this.config.mobileScrollOffset : this.config.scrollOffset;

    Scroll.scrollTo(target, {
      offset,
      force: true,
    });
  }

  setupContentObservers() {
    if (typeof ResizeObserver !== "undefined") {
      this.contentResizeObserver = new ResizeObserver(() => {
        this.refreshLayout();
      });
      this.contentResizeObserver.observe(this.richText);
    }

    const images = Array.from(this.richText.querySelectorAll("img"));

    images.forEach((image) => {
      if (image.complete) return;

      const handleImageReady = () => {
        this.refreshLayout();
      };

      image.addEventListener("load", handleImageReady, { passive: true });
      image.addEventListener("error", handleImageReady, { passive: true });
      this.imageListeners.push({ element: image, handler: handleImageReady });
    });
  }

  handleScroll(data = {}) {
    this.updateScrollState(data);
  }

  handleResize() {
    this.refreshLayout();
  }

  refreshLayout() {
    if (this.refreshFrame) return;

    this.refreshFrame = window.requestAnimationFrame(() => {
      this.refreshFrame = 0;
      Scroll.resize?.();
      this.updateScrollState({
        scroll: Scroll.scroll,
        limit: Scroll.limit,
        progress: Scroll.progress,
      });
    });
  }

  updateScrollState(data = {}) {
    this.updateProgressBar(data);
    this.updateActiveTOC();
  }

  updateProgressBar(data = {}) {
    if (!this.progressBar) return;

    const scroll = Number.isFinite(data.scroll) ? data.scroll : Scroll.scroll;
    const limit = Number.isFinite(data.limit) ? data.limit : Scroll.limit;
    const progress = Number.isFinite(data.progress)
      ? data.progress
      : limit > 0
        ? scroll / limit
        : 0;

    this.progressBar.style.transform = `scaleX(${progress})`;
  }

  updateActiveTOC() {
    if (!this.sections.length || !this.tocLinks.length) return;

    const trigger = window.innerHeight * this.config.activeOffset;
    let currentSection = this.sections[0];

    for (const section of this.sections) {
      if (section.getBoundingClientRect().top <= trigger) {
        currentSection = section;
        continue;
      }

      break;
    }

    this.tocLinks.forEach((link) => {
      const isActive = link.dataset.id === currentSection.id;
      link.classList.toggle("active", isActive);

      if (isActive && this.toggleButton) {
        const label = this.toggleButton.querySelector(".toc-toggle-label");
        // if (label) label.textContent = link.textContent.trim();
      }
    });
  }

  destroy() {
    this.close();
    this.cleanupScroll?.();
    this.cleanupResize?.();
    this.cleanupScroll = null;
    this.cleanupResize = null;

    this.linkListeners.forEach(({ element, handler }) => {
      element.removeEventListener("click", handler);
    });
    this.linkListeners = [];

    this.imageListeners.forEach(({ element, handler }) => {
      element.removeEventListener("load", handler);
      element.removeEventListener("error", handler);
    });
    this.imageListeners = [];

    this.contentResizeObserver?.disconnect();
    this.contentResizeObserver = null;

    if (this.refreshFrame) {
      window.cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = 0;
    }

  }
}

export default function (element, dataset) {
  const toc = new BlogTOC(element, dataset);
  toc.init();

  onDestroy(() => {
    toc.destroy();
  });

  return toc;
}
